import { MenuItem } from "prosemirror-menu";
import type { EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { type DomIconSpec, closePanelIconSpec, moreOptionsIconSpec } from "../icons";
import { type ToolbarPanel, toolbarPanelPluginKey } from "../plugins/toolbar-panel";

import { t } from "i18n/i18n";

// focusVisible is not yet in TS's FocusOptions but is supported in all modern browsers.
interface FocusOptionsExtended extends FocusOptions {
  focusVisible?: boolean;
}
const extendedFocusOptions: FocusOptionsExtended = { focusVisible: true };

function findFirstVisibleButton(toolbar: HTMLElement): HTMLButtonElement | null {
  for (const item of toolbar.querySelectorAll<HTMLElement>(".ProseMirror-menuitem")) {
    if (item.style.display !== "none") {
      const btn = item.querySelector<HTMLButtonElement>("button");
      if (btn) return btn;
    }
  }
  return null;
}

/**
 * Transfers keyboard focus to the first visible button in the toolbar after a panel switch.
 *
 * prosemirror-menu uses a roving tabindex across all toolbar buttons, keeping exactly one at
 * tabindex="0" at a time. When a panel switch hides the currently-tabbable button, that
 * button's tabindex="0" remains on a hidden element, so Tab from the editor skips the toolbar
 * entirely. This function moves the tabindex to the first visible button in the new panel so
 * Tab navigation works correctly.
 *
 * For keyboard activation, also calls .focus() so the user lands directly on the new panel's first button.
 * For mouse activation, only the tabindex is updated — focus deliberately stays in the editor.
 */
export function transferToolbarFocus(view: EditorView, isKeyboard: boolean): void {
  const wrapper = view.dom.closest<HTMLElement>(".ProseMirror-menubar-wrapper");
  const toolbar = wrapper?.querySelector<HTMLElement>("[role=toolbar]");
  if (!toolbar) return;

  const next = findFirstVisibleButton(toolbar);
  if (!next) return;

  const prev = toolbar.querySelector<HTMLElement>("[tabindex='0']");
  if (prev && prev !== next) prev.setAttribute("tabindex", "-1");

  next.setAttribute("tabindex", "0");
  if (isKeyboard) next.focus(extendedFocusOptions);
}

/**
 * Creates a toolbar MenuItem that navigates from sourcePanel to targetPanel.
 * Visible only when sourcePanel is active.
 */
export function createGoToPanelItem(
  targetPanel: ToolbarPanel,
  sourcePanel: ToolbarPanel,
  icon: DomIconSpec,
  title: string,
  cssClass?: string,
): MenuItem {
  return new MenuItem({
    title,
    icon, // prosemirror-menu's path spec renders filled shapes only; stroke attributes require a DOM node
    class: cssClass,
    select(state: EditorState) {
      return (toolbarPanelPluginKey.getState(state)?.activePanel ?? "main") === sourcePanel;
    },
    run(state, dispatch, view, event) {
      const isKeyboard = (event as MouseEvent | undefined)?.detail === 0; // detail = click count
      view?.focus(); // keeps :focus-within alive during dispatch; no-op for mouse
      dispatch(state.tr.setMeta(toolbarPanelPluginKey, { activePanel: targetPanel }));
      transferToolbarFocus(view, isKeyboard);
    },
  });
}

export const createMoreOptionsItem = createGoToPanelItem(
  "moreOptions",
  "main",
  moreOptionsIconSpec,
  t("worksheet.rich_text_editor.show_more_options"),
);

export function createCloseItemForPanel(sourcePanel: ToolbarPanel): MenuItem {
  return createGoToPanelItem(
    "main",
    sourcePanel,
    closePanelIconSpec,
    t("worksheet.rich_text_editor.close_panel"),
    "close-panel-item",
  );
}
